import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    X, Loader2, CheckCircle2, Building2, User, AlertTriangle, Undo2,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
    DiffTable, ImportResultLinks, PersonRow, VerdictBadge,
    pbValue, defaultDecision, willWrite,
} from './ImportDiffParts';
import { isEmpty } from '../../utils/pitchbookDiff';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Main dialog ──────────────────────────────────────────────────────────────

export default function ImportDiffDialog({ pitchbookId, recordName, personFocusId = null, onClose, onCommitted }) {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [committing, setCommitting] = useState(false);
    const [result, setResult] = useState(null);
    const [undoing, setUndoing] = useState(false);
    const [undone, setUndone] = useState(false);

    const [includeCompany, setIncludeCompany] = useState(true);
    const [selectedPersons, setSelectedPersons] = useState({});
    const [expandedPersons, setExpandedPersons] = useState({});
    // {company: {col: policy}, persons: {pbId: {col: policy}}} — only deviations.
    const [companyDecisions, setCompanyDecisions] = useState({});
    const [personDecisions, setPersonDecisions] = useState({});

    const loadPreview = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.post(`${API}/pitchbook/manual-sync/preview`,
                { pitchbook_id: pitchbookId }, { headers: getAuthHeader() });
            setPreview(res.data);
            setIncludeCompany(true);
            // Focused on one person → select just them; otherwise default to
            // everyone (an explicit [] tells the backend to import no people).
            setSelectedPersons(Object.fromEntries(
                (res.data.persons || []).map((p) => [
                    p.pitchbook_id,
                    personFocusId ? p.pitchbook_id === personFocusId : true,
                ])
            ));
            if (personFocusId) setExpandedPersons({ [personFocusId]: true });
        } catch (e) {
            const status = e.response?.status;
            if (status === 422) setError(e.response?.data?.detail || 'This record is too large to import in one go.');
            else if (status === 404) setError('This record was not found in the indexed PitchBook data.');
            else if (status === 409) setError(e.response?.data?.detail?.drift_report
                ? 'The feed schema changed — resolve the drift before importing.'
                : (e.response?.data?.detail || 'No indexed PitchBook data yet.'));
            else setError('Failed to load the import preview.');
        } finally {
            setLoading(false);
        }
    }, [pitchbookId, personFocusId, getAuthHeader]);

    useEffect(() => { loadPreview(); }, [loadPreview]);

    const setCompanyDecision = (col, policy) =>
        setCompanyDecisions((s) => ({ ...s, [col]: policy }));
    const setPersonDecision = (pbId, col, policy) =>
        setPersonDecisions((s) => ({ ...s, [pbId]: { ...(s[pbId] || {}), [col]: policy } }));

    // Only ship decisions that deviate from the saved mapping policy.
    const buildOverrides = useCallback(() => {
        const overrides = { company: {}, persons: {} };
        const fieldsByCol = Object.fromEntries((preview?.company?.fields || []).map((f) => [f.csv_column, f]));
        for (const [col, policy] of Object.entries(companyDecisions)) {
            const row = fieldsByCol[col];
            if (row && policy !== defaultDecision(row)) overrides.company[col] = policy;
        }
        for (const p of preview?.persons || []) {
            const personCols = personDecisions[p.pitchbook_id] || {};
            const byCol = Object.fromEntries((p.fields || []).map((f) => [f.csv_column, f]));
            const out = {};
            for (const [col, policy] of Object.entries(personCols)) {
                const row = byCol[col];
                if (row && policy !== defaultDecision(row)) out[col] = policy;
            }
            if (Object.keys(out).length) overrides.persons[p.pitchbook_id] = out;
        }
        return overrides;
    }, [preview, companyDecisions, personDecisions]);

    const summary = useMemo(() => {
        if (!preview) return { writes: 0, kept: 0 };
        let writes = 0;
        let kept = 0;
        const tally = (fields, decisions, active) => {
            if (!active) return;
            for (const f of fields || []) {
                if (!f.target || f.policy === 'pitchbook_tab_only' || isEmpty(pbValue(f))) continue;
                const d = decisions[f.csv_column] ?? defaultDecision(f);
                if (willWrite(f, d)) writes += 1;
                else kept += 1;
            }
        };
        tally(preview.company?.fields, companyDecisions, includeCompany);
        for (const p of preview.persons || []) {
            tally(p.fields, personDecisions[p.pitchbook_id] || {}, !!selectedPersons[p.pitchbook_id]);
        }
        return { writes, kept };
    }, [preview, companyDecisions, personDecisions, includeCompany, selectedPersons]);

    const runCommit = async () => {
        setCommitting(true);
        try {
            const res = await axios.post(`${API}/pitchbook/manual-sync/commit`, {
                preview_id: preview.preview_id,
                include_company: includeCompany,
                selected_person_ids: Object.entries(selectedPersons)
                    .filter(([, v]) => v).map(([pid]) => pid),
                field_overrides: buildOverrides(),
            }, { headers: getAuthHeader() });
            setResult(res.data);
            onCommitted?.();
        } catch (e) {
            if (e.response?.status === 409) {
                toast.error(e.response?.data?.detail || 'Preview stale — reloading');
                loadPreview();
            } else if (e.response?.status === 422) {
                toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Invalid import selection');
            } else {
                toast.error('Import failed');
            }
        } finally {
            setCommitting(false);
        }
    };

    const runUndo = async () => {
        if (!result?.run_id) return;
        setUndoing(true);
        try {
            await axios.post(`${API}/pitchbook/manual-sync/runs/${result.run_id}/undo`,
                {}, { headers: getAuthHeader() });
            setUndone(true);
            toast.success('Import reverted');
            onCommitted?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to revert');
        } finally {
            setUndoing(false);
        }
    };

    const selectedPersonCount = Object.values(selectedPersons).filter(Boolean).length;
    const canCommit = includeCompany || selectedPersonCount > 0;
    const company = preview?.company;
    const isUpdate = company?.verdict === 'update' || company?.verdict === 'skip_no_change';
    const stats = result?.projection_stats;

    const setAllPersons = (selected) =>
        setSelectedPersons(Object.fromEntries((preview?.persons || []).map((p) => [p.pitchbook_id, selected])));

    return createPortal((
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
                    <div className="min-w-0">
                        <h3 className="font-semibold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Import to CRM — {recordName || pitchbookId}
                        </h3>
                        <p className="text-xs text-stone-500 mt-0.5">
                            Review the side-by-side diff, choose what each field should do, then import.
                            Nothing is written until you confirm.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-700 shrink-0 ml-3"><X className="w-5 h-5" /></button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5">
                    {loading && (
                        <div className="py-16 text-center text-stone-400">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                            <p className="text-xs">Building the preview…</p>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                            <p className="text-sm text-amber-800">{error}</p>
                        </div>
                    )}

                    {!loading && !error && preview && !result && (
                        <div className="space-y-5">
                            {/* Company block */}
                            <div>
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <label className="flex items-center gap-2 min-w-0">
                                        <input
                                            type="checkbox"
                                            checked={includeCompany}
                                            onChange={(e) => setIncludeCompany(e.target.checked)}
                                            className="rounded border-stone-300 text-stone-900 focus:ring-stone-700"
                                        />
                                        <Building2 className="w-4 h-4 text-stone-500 shrink-0" />
                                        <h4 className="font-semibold text-stone-800 text-sm truncate">
                                            {company.entity === 'investor' ? 'Investor' : 'Company'}
                                        </h4>
                                        <VerdictBadge verdict={company.verdict} />
                                    </label>
                                    {company.matched_crm_account && (
                                        <span className="text-xs text-stone-500 truncate">→ {company.matched_crm_account.name}</span>
                                    )}
                                </div>
                                {includeCompany && (
                                    <DiffTable
                                        fields={company.fields}
                                        decisions={companyDecisions}
                                        onDecision={setCompanyDecision}
                                        isUpdate={isUpdate}
                                    />
                                )}
                            </div>

                            {preview.warnings?.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                                    {preview.warnings.map((w, i) => <div key={i}>• {w}</div>)}
                                </div>
                            )}

                            {/* People */}
                            {preview.persons?.length > 0 && (
                                <div>
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4 text-stone-500" />
                                            <h4 className="font-semibold text-stone-800 text-sm">
                                                People ({selectedPersonCount}/{preview.persons.length} selected)
                                            </h4>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs">
                                            <button type="button" onClick={() => setAllPersons(true)} className="text-stone-600 hover:text-stone-900">Select all</button>
                                            <span className="text-stone-300">·</span>
                                            <button type="button" onClick={() => setAllPersons(false)} className="text-stone-600 hover:text-stone-900">Select none</button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        {preview.persons.map((p) => (
                                            <PersonRow
                                                key={p.pitchbook_id}
                                                person={p}
                                                selected={!!selectedPersons[p.pitchbook_id]}
                                                expanded={!!expandedPersons[p.pitchbook_id]}
                                                onToggleSelect={(v) => setSelectedPersons((s) => ({ ...s, [p.pitchbook_id]: v }))}
                                                onToggleExpand={() => setExpandedPersons((s) => ({ ...s, [p.pitchbook_id]: !s[p.pitchbook_id] }))}
                                                decisions={personDecisions[p.pitchbook_id] || {}}
                                                onDecision={(col, policy) => setPersonDecision(p.pitchbook_id, col, policy)}
                                                highlighted={p.pitchbook_id === personFocusId}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {!includeCompany && selectedPersonCount > 0 && company.verdict === 'create' && (
                                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>This company is not in the CRM yet — selected people will be imported without a company link.</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Result */}
                    {result && (
                        <div className="text-center py-8 space-y-4">
                            {undone ? (
                                <>
                                    <Undo2 className="w-12 h-12 text-stone-400 mx-auto" />
                                    <h4 className="font-semibold text-stone-800">Import reverted</h4>
                                    <p className="text-sm text-stone-500">All changes from this import were rolled back.</p>
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
                                    <h4 className="font-semibold text-stone-800">Import completed</h4>
                                    <div className="inline-flex flex-col gap-1 text-sm text-stone-600">
                                        <span>Companies: {stats?.company?.created || 0} created · {stats?.company?.updated || 0} updated · {stats?.company?.skipped || 0} unchanged</span>
                                        <span>People: {stats?.person?.created || 0} created · {stats?.person?.updated || 0} updated · {stats?.person?.skipped || 0} unchanged</span>
                                    </div>
                                    {result.warnings?.length > 0 && (
                                        <div className="max-w-xl mx-auto bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 text-left">
                                            {result.warnings.map((w, i) => <div key={i}>• {w}</div>)}
                                        </div>
                                    )}
                                    <ImportResultLinks links={result.links} />
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-4 border-t border-stone-200">
                    <div className="text-xs text-stone-500">
                        {!loading && !error && preview && !result && (
                            <span>
                                Will write <strong className="text-stone-800">{summary.writes}</strong> field{summary.writes === 1 ? '' : 's'}
                                {summary.kept > 0 && <> · keep <strong className="text-stone-700">{summary.kept}</strong> existing</>}
                            </span>
                        )}
                        {result && !undone && (
                            <button onClick={runUndo} disabled={undoing}
                                className="flex items-center gap-1.5 text-stone-500 hover:text-rose-600">
                                {undoing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                                Undo this import
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {result ? (
                            <Button onClick={onClose}>Done</Button>
                        ) : (
                            <>
                                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                                <Button onClick={runCommit} disabled={committing || loading || !!error || !canCommit}>
                                    {committing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                                    Import {includeCompany ? '1 record' : '0 records'} + {selectedPersonCount} {selectedPersonCount === 1 ? 'person' : 'people'}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    ), document.body);
}
